import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useConversation, usePatchConversation, useDeleteConversation } from '../../hooks/useConversations';
import { useSendMessage, useRegenerateMessage } from '../../hooks/useSendMessage';
import { useStatusStream } from '../../hooks/useStatusStream';
import { Composer } from './Composer';
import { ConversationSkeleton } from './ConversationSkeleton';
import { ConversationTabs } from './ConversationTabs';
import { EmptyState } from './EmptyState';
import { MessageBubble } from './MessageBubble';
import { StatusDisplay } from '../status/StatusDisplay';
import { useToast } from '../toast/Toast';
import { useConfirm } from '../dialog/ConfirmDialog';
import type { Message, PreferredFormat } from '../../types/api';

// =============================================================================
// Persona theming — gives each conversation a recognizable visual identity
// (badge in the header + soft tinted background on the body).
// =============================================================================

interface PersonaTheme {
  label: string;            // "CFO", "COO" — short uppercase
  subtitle: string;         // "Chief Financial Officer"
  /**
   * Gradient stops keyed to the HomePage's persona colors. Used as
   * inline `style` (not class names) so the icon tile here is pixel-
   * identical to the HomePage card. Same source of truth as
   * `ACCENT_GRADIENTS` in HomePage.tsx — keep them in sync.
   */
  gradient: { g1: string; g2: string };
  /**
   * Very-low-opacity rgba in the persona's light-end hue. Drives a
   * single radial gradient over the cream body surface so each
   * conversation reads as that executive's "room" without going
   * candy-bright.
   */
  bodyTint: string;
  Icon: () => JSX.Element;  // svg icon
}

const BriefcaseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
  </svg>
);

const GearIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const MegaphoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m3 11 18-5v12L3 14v-3z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

const CompassIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
);

const PERSONA_THEMES: Record<string, PersonaTheme> = {
  cfo: {
    label: 'CFO',
    subtitle: 'Chief Financial Officer',
    // wb-900 → wb-500 (matches HomePage CFO card)
    gradient: { g1: '#142755', g2: '#4A82C7' },
    bodyTint: 'rgba(74, 130, 199, 0.0512)',
    Icon: BriefcaseIcon,
  },
  coo: {
    label: 'COO',
    subtitle: 'Chief Operating Officer',
    // teal-900 → teal-400 (matches HomePage COO card)
    gradient: { g1: '#134e4a', g2: '#2dd4bf' },
    bodyTint: 'rgba(45, 212, 191, 0.0512)',
    Icon: GearIcon,
  },
  cmo: {
    label: 'CMO',
    subtitle: 'Chief Marketing Officer',
    // cmo-900 → cmo-400
    gradient: { g1: '#4C1D95', g2: '#A78BFA' },
    bodyTint: 'rgba(167, 139, 250, 0.0512)',
    Icon: MegaphoneIcon,
  },
  ceo: {
    label: 'CEO',
    subtitle: 'Chief Executive Officer',
    // amber-800 → amber-500 (matches HomePage CEO card)
    gradient: { g1: '#92400E', g2: '#F59E0B' },
    bodyTint: 'rgba(245, 158, 11, 0.0512)',
    Icon: CompassIcon,
  },
};

// Fallback for unknown / future personas — slate-themed, neutral.
const FALLBACK_THEME: PersonaTheme = {
  label: '',
  subtitle: '',
  gradient: { g1: '#475569', g2: '#94a3b8' },
  bodyTint: 'rgba(100, 116, 139, 0.04)',
  Icon: BriefcaseIcon,
};

export function ConversationView({ conversationId }: { conversationId: string }) {
  const { data, isLoading, error } = useConversation(conversationId);
  const send = useSendMessage(conversationId);
  const regen = useRegenerateMessage(conversationId);
  const patch = usePatchConversation();
  const remove = useDeleteConversation();
  const status = useStatusStream(conversationId, send.isPending || regen.isPending);
  const bottomRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  // Which conversationId we've already run the "smart landing" evaluation
  // for. We store the id (not a boolean) because react-router reuses this
  // component instance when only the :conversationId route param changes —
  // ConversationView is not keyed by conversationId — so a boolean guard
  // would stay true forever and every conversation after the first would
  // never auto-land. Keying on the id makes the evaluation run exactly once
  // per conversation while still ignoring in-conversation data updates.
  const evaluatedLandingForRef = useRef<string | null>(null);

  // Smart default: if this conversation ALREADY has charts when the user
  // arrives cold (deep link, refresh, sidebar click), the dashboard is the
  // more useful landing surface — first thing the M&A audience should see
  // is the analytics, not the chat thread. The Conversation tab links with
  // `?view=chat` to opt out.
  //
  // Important: this runs ONCE per conversationId, on its first data load. We
  // do NOT re-evaluate when the assistant adds a chart mid-conversation.
  // Earlier behavior re-ran on every `data` change, which meant generating a
  // chart in a live conversation auto-jumped the user to the dashboard and
  // broke their flow.
  useEffect(() => {
    if (!data) return;
    if (evaluatedLandingForRef.current === conversationId) return;
    evaluatedLandingForRef.current = conversationId;

    const params = new URLSearchParams(location.search);
    if (params.get('view') === 'chat') return;
    const hasCharts = data.messages.some((m) => m.chartSpec);
    if (hasCharts) {
      navigate(`/c/${conversationId}/dashboard`, { replace: true });
    }
  }, [data, location.search, navigate, conversationId]);

  // Auto-scroll to bottom on new messages or in-flight start (unless we're
  // jumping to a specific message via URL hash).
  useEffect(() => {
    if (location.hash) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages.length, send.isPending, location.hash]);

  // Hash-jump: scroll to and briefly highlight a specific message
  useEffect(() => {
    if (!data) return;
    if (!location.hash) return;
    const id = location.hash.slice(1);
    if (!id) return;
    // Wait a tick so the DOM has rendered
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedMessageId(id);
        setTimeout(() => setHighlightedMessageId(null), 2400);
      }
    }, 80);
    return () => clearTimeout(t);
  }, [data, location.hash]);

  const isInFlight = send.isPending || regen.isPending;

  function handleSend(content: string, preferredFormat: PreferredFormat) {
    send.mutate(
      { content, preferredFormat },
      {
        onSuccess: (r) => {
          if (r.cacheHit) toast.show('Cache hit — response served without spending tokens.', 'success');
        },
        onError: (err) => toast.show(`Error: ${(err as Error).message}`, 'error'),
      },
    );
  }

  function handleRegenerate(msgId: string) {
    regen.mutate({ msgId });
  }

  function handleEditUserMessage(_userMsgId: string, newContent: string) {
    // Edit pattern: regenerate the next assistant message with replaced content.
    // We need to find the assistant message that came AFTER this user message.
    if (!data) return;
    const idx = data.messages.findIndex((m) => m.id === _userMsgId);
    const assistantNext = data.messages.slice(idx + 1).find((m) => m.role === 'assistant');
    if (assistantNext) {
      regen.mutate({ msgId: assistantNext.id, content: newContent });
    } else {
      // No subsequent assistant — just resend
      send.mutate({ content: newContent, preferredFormat: 'auto' });
    }
  }

  async function handleCopyMessage(content: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(content);
      toast.show('Message copied to clipboard.', 'success');
    } catch {
      // Older browsers / insecure contexts: fall back to a hidden textarea trick.
      const ta = document.createElement('textarea');
      ta.value = content;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.show('Message copied to clipboard.', 'success');
      } catch {
        toast.show('Could not copy — please copy manually.', 'error');
      } finally {
        document.body.removeChild(ta);
      }
    }
  }

  async function handleDeleteMessage(msgId: string): Promise<void> {
    if (!data) return;
    const ok = await confirm.confirm({
      title: 'Remove message',
      description:
        'This only removes it from your view — the backend doesn\'t support per-message delete yet, so it will reappear on refresh. For permanent deletion, use "Delete conversation" in the header.',
      confirmLabel: 'Remove from view',
      cancelLabel: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    // Optimistic local-only removal: mutate the React Query cache so the
    // message disappears immediately. A page refresh restores it.
    queryClient.setQueryData<typeof data>(
      ['conversation', conversationId],
      (prev) =>
        prev
          ? {
              ...prev,
              messages: prev.messages.filter((m: Message) => m.id !== msgId),
            }
          : prev,
    );
    toast.show('Message removed from view (does not persist).', 'info');
  }

  function togglePin() {
    if (!data) return;
    patch.mutate({ id: data.conversation.id, fields: { pinned: !data.conversation.pinned } });
  }

  async function deleteConv() {
    if (!data) return;
    const ok = await confirm.confirm({
      title: 'Delete conversation',
      description: `"${data.conversation.title}" — this action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    remove.mutate(data.conversation.id, {
      onSuccess: () => {
        // Navigate via the browser API (react-router's location lacks .assign)
        window.location.assign('/');
      },
      onError: (err) => toast.show(`Error: ${(err as Error).message}`, 'error'),
    });
  }

  if (isLoading) {
    return <ConversationSkeleton />;
  }
  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-700">We couldn't load the conversation.</p>
        <p className="mt-1 text-sm text-slate-500">{(error as Error).message}</p>
      </div>
    );
  }
  if (!data) return null;

  const messages = data.messages;
  const theme = PERSONA_THEMES[data.conversation.personaId] ?? FALLBACK_THEME;
  const PersonaIcon = theme.Icon;

  return (
    <div
      // Persona-tinted session surface lives ONCE here on the root so
      // header / body / composer all inherit the same color. Avoids
      // the "beige strip" effect when each piece set its own bg.
      className="flex h-full flex-col"
      style={{
        backgroundColor: '#f4f3ee',
        backgroundImage: `linear-gradient(${theme.bodyTint}, ${theme.bodyTint})`,
      }}
    >
      {/* Header — transparent so the root persona wash shows through.
          Border-bottom is the only visual separator. */}
      {/* Row layout starts at lg, not sm — until 1024px the app sidebar
          (256px) leaves too little width for persona block + tabs + actions
          on one line (verified overlapping at 768), which pushed the
          Pin/Delete buttons off-screen. */}
      <header className="flex flex-col gap-3 border-b border-slate-900/[0.06] bg-transparent px-4 py-3.5 lg:flex-row lg:items-center lg:justify-between sm:px-7 sm:py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Persona icon tile — pixel-identical to the HomePage card so
              users recognize the visual identity at a glance. Gradient
              comes from PERSONA_THEMES, mirrored from ACCENT_GRADIENTS. */}
          <div
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] text-white"
            style={{
              background: `linear-gradient(135deg, ${theme.gradient.g1} 0%, ${theme.gradient.g2} 100%)`,
              boxShadow:
                '0 3px 10px rgba(12,26,43,0.16), 0 0 0 1px rgba(255,255,255,0.0512) inset',
            }}
            aria-hidden="true"
          >
            <PersonaIcon />
          </div>
          <div className="min-w-0">
            {/* Identifier row: persona label in serif (echoing HomePage
                role names) + subtitle in small caps. We DO NOT show
                "Untitled" as a placeholder — the persona identity is
                enough on its own; once the user sends their first
                message the backend auto-titles and the h1 fills in. */}
            <div className="flex items-baseline gap-2">
              {theme.label && (
                <span className="text-lg font-semibold leading-none tracking-[-0.02em] text-slate-900 sm:text-xl">
                  {theme.label}
                </span>
              )}
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                {theme.subtitle || 'Executive'}
              </span>
            </div>
            {data.conversation.title && (
              <h1 className="mt-0.5 truncate text-[13.5px] font-medium text-slate-700 sm:text-sm">
                {data.conversation.title}
              </h1>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConversationTabs
            conversationId={data.conversation.id}
            current="chat"
            chartCount={messages.filter((m) => m.chartSpec).length}
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={togglePin}
              className="btn-ghost h-9 min-h-0 px-2 text-xs"
              aria-pressed={data.conversation.pinned}
            >
              {data.conversation.pinned ? 'Pinned' : 'Pin'}
            </button>
            <button
              type="button"
              onClick={deleteConv}
              className="btn-ghost h-9 min-h-0 px-2 text-xs text-red-600 hover:bg-red-50"
              aria-label="Delete conversation"
            >
              Delete
            </button>
          </div>
        </div>
      </header>

      {/* Body: transparent — inherits the root's persona-tinted cream
          wash. Cards/bubbles inside this surface define themselves
          with borders, not by sitting on white. */}
      <div className="flex-1 overflow-y-auto bg-transparent">
        {messages.length === 0 ? (
          <EmptyState
            onPick={(t) => handleSend(t, 'auto')}
            personaId={data.conversation.personaId}
          />
        ) : (
          <div className="mx-auto w-full max-w-3xl space-y-4 px-3 py-4 sm:px-6 sm:py-6">
            {messages.map((m) => (
              <div
                key={m.id}
                className={`transition-colors duration-700 rounded-2xl ${
                  highlightedMessageId === m.id ? 'ring-2 ring-wb-400 ring-offset-2 ring-offset-slate-50' : ''
                }`}
              >
                <MessageBubble
                  message={m}
                  pinned={data.conversation.pinned}
                  onPin={togglePin}
                  onEdit={m.role === 'user' ? (newContent) => handleEditUserMessage(m.id, newContent) : undefined}
                  onRegenerate={m.role === 'assistant' ? () => handleRegenerate(m.id) : undefined}
                  onCopy={() => void handleCopyMessage(m.content)}
                  onDelete={() => void handleDeleteMessage(m.id)}
                />
              </div>
            ))}

            {(send.error || regen.error) && (
              <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
                Something went wrong on the query side.{' '}
                <button
                  type="button"
                  onClick={() => {
                    send.reset();
                    regen.reset();
                  }}
                  className="underline"
                >
                  Retry
                </button>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {(send.isPending || regen.isPending) && (
        <div className="px-3 sm:px-6 pb-3">
          <StatusDisplay events={status} inFlight={true} />
        </div>
      )}

      <Composer onSend={handleSend} disabled={isInFlight} />
    </div>
  );
}
